import { useCallback, useMemo, useState } from 'react';

import { CHAT_MESSAGES } from '@/components/emotion-detector/constants';
import { useApi } from '@/hooks/api';
import { ChatService } from '@/services/emotion-detector';
import { TopEmotion } from '@/utils/emotion-detector';

export type ChatMessage = { role: 'user' | 'assistant'; content: string };

export function useEmotionChat(journalId: string | null) {
	const apiRequest = useApi();
	const chatService = useMemo(() => new ChatService(apiRequest), [apiRequest]);

	const [chatActive, setChatActive] = useState(false);
	const [chatMessages, setMessages] = useState<ChatMessage[]>([]);
	const [input, setInput] = useState('');
	const [sending, setSending] = useState(false);

	const sendMessageToBackend = useCallback(
		async (content: string) => {
			if (!journalId) return;

			setSending(true);
			try {
				const res = await chatService.sendMessage(journalId, content);

				// Add both user message and AI response to chat
				setMessages(prev => [
					...prev,
					{ role: 'user', content: res.user_message },
					{ role: 'assistant', content: res.ai_response },
				]);
			} catch (e: unknown) {
				setMessages(prev => [...prev, { role: 'assistant', content: CHAT_MESSAGES.ERROR_SENDING }]);
			} finally {
				setSending(false);
			}
		},
		[journalId, chatService],
	);

	// Note: Conversation cleanup is handled by the backend when journal is deleted
	// No explicit endConversation API needed since chat conversations are tied to journal lifecycle

	const startChatWith = useCallback(
		async (emotion: TopEmotion) => {
			if (!journalId) return;

			try {
				setChatActive(true);
				// Clear messages and start fresh conversation
				setMessages([]);

				// Create conversation with backend
				const conversationRes = await chatService.createConversation(journalId);

				// Add initial AI message to chat
				setMessages([
					{
						role: 'assistant',
						content: conversationRes.initial_message || CHAT_MESSAGES.DEFAULT_GREETING,
					},
				]);
			} catch (e: unknown) {
				setMessages([
					{
						role: 'assistant',
						content: CHAT_MESSAGES.ERROR_STARTING,
					},
				]);
			}
		},
		[journalId, chatService],
	);

	const handleSend = useCallback(async () => {
		if (!input.trim() || sending) return;
		const message = input.trim();
		setInput('');
		await sendMessageToBackend(message);
	}, [input, sending, sendMessageToBackend]);

	const resetChat = useCallback(() => {
		setChatActive(false);
		setMessages([]);
		setInput('');
		setSending(false);
	}, []);

	return {
		// State
		chatActive,
		chatMessages,
		input,
		sending,

		// Actions
		setInput,
		setChatActive,
		startChatWith,
		handleSend,
		resetChat,
	};
}
